import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';

export interface PaginationProps {
  /** 1-based current page. */
  page: number;
  /** Total number of pages. */
  pageCount: number;
  /** Total item count across all pages. */
  total: number;
  /** 1-based index of the first item shown. */
  from: number;
  /** 1-based index of the last item shown. */
  to: number;
  onPageChange: (page: number) => void;
  /** Singular noun for the range label, e.g. "worker" → "worker(s)". */
  itemLabel?: string;
  className?: string;
}

// Modelled on `<Button variant="outline" size="sm">` — kept as a local constant
// rather than the component itself so the pager stays free of a circular
// import and can size its two controls independently of the button scale. It
// now uses that independence for one property, and only one:
// `rounded-xs` (8px) and not the `rounded-control` (10px) the rest of the small
// controls take: the handoff's pager cap is its own shape — `min-height: 30px;
// padding: 0 9px; border-radius: 8px` (Hadir Employees.dc.html:325) — a tighter
// corner on a smaller cap. The 30px height is NOT copied; 32px is the floor the
// rest of the small controls sit at and a one-off 30px pager would be the only
// control in the app off that rail.
const btn = cn(
  'inline-flex min-h-8 items-center rounded-xs border border-border px-btn-x-sm py-btn-y-sm text-label text-foreground',
  'transition-colors duration-fast ease-standard hover:bg-muted',
  'disabled:pointer-events-none disabled:opacity-50',
  focusRing,
  '[@media(pointer:coarse)]:min-h-touch'
);

/**
 * Compact pager for client-paginated lists. Renders a "Showing X–Y of N"
 * range plus Previous / Next controls. Returns null when there's a single page
 * (nothing to page), so callers can drop it in unconditionally.
 *
 * Uses logical alignment (`justify-between`, `text-start`) so it mirrors under
 * RTL, and semantic tokens so it themes with light/dark.
 */
export function Pagination({
  page,
  pageCount,
  total,
  from,
  to,
  onPageChange,
  itemLabel = 'item',
  className,
}: PaginationProps): JSX.Element | null {
  if (pageCount <= 1) return null;
  const noun = total === 1 ? itemLabel : `${itemLabel}s`;
  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-component border-t border-border py-cell-y',
        // `px-tile` (20px), not `px-cell-x` (16px): the pager band is the table
        // shell's own footer and the handoff insets it on the shell's rail —
        // `padding: 13px 20px` (Hadir Employees.dc.html:322) — so the range line
        // starts under the first column rather than 4px inside it.
        'px-tile',
        // The band carries its own type the way the mock's does: 12.5px in
        // `--foreground-tertiary` (#6A6659), which the two readouts below
        // inherit. `font-normal` because `text-label` is a 600 step and this is
        // a sentence, not a label — the only emphasis in the band belongs to the
        // figures, which set their own weight.
        'text-label font-normal text-foreground-tertiary',
        className
      )}
    >
      <p aria-live="polite">
        Showing{' '}
        <span className="font-medium text-foreground">
          {from}–{to}
        </span>{' '}
        of <span className="font-medium text-foreground">{total}</span> {noun}
      </p>
      <div className="flex items-center gap-element">
        <button
          type="button"
          className={btn}
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          aria-label="Previous page"
        >
          Previous
        </button>
        <span className="px-1">
          Page {page} of {pageCount}
        </span>
        <button
          type="button"
          className={btn}
          onClick={() => onPageChange(page + 1)}
          disabled={page >= pageCount}
          aria-label="Next page"
        >
          Next
        </button>
      </div>
    </div>
  );
}
